import { useCallback } from "react";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../services/queryKeys";
import {
  fetchTenantMemberships,
  fetchTenantWorkspaces,
  selectTenant,
} from "../services/tenantContext/tenantContextApi";
import { activeTenantName } from "../services/tenantContext/tenantContextMappers";
import type {
  MembershipRead,
  SelectTenantResponse,
  TenantWorkspaceRead,
} from "../services/tenantContext/tenantContext.types";
import { getActiveTenantId } from "../services/workspace/activeWorkspace";

/**
 * Tenant context changes only when an admin edits memberships, so a short stale
 * window lets the selection page, the guard and the legacy workspace hydration
 * share one network response instead of racing three identical requests.
 */
const TENANT_CONTEXT_STALE_TIME = 5 * 60 * 1000;

/** `retry: false` — a 401/403/404 here is a routing decision, not a blip to retry. */
export const tenantMembershipsQuery = () =>
  queryOptions<MembershipRead[]>({
    queryKey: queryKeys.tenantContext.memberships(),
    queryFn: fetchTenantMemberships,
    staleTime: TENANT_CONTEXT_STALE_TIME,
    retry: false,
  });

export const tenantWorkspacesQuery = (tenantId: string) =>
  queryOptions<TenantWorkspaceRead[]>({
    queryKey: queryKeys.tenantContext.workspaces(tenantId),
    queryFn: () => fetchTenantWorkspaces(tenantId),
    staleTime: TENANT_CONTEXT_STALE_TIME,
    retry: false,
  });

/** Active tenant memberships for the bearer — `[]` means no tenant to select. */
export const useTenantMemberships = () => useQuery(tenantMembershipsQuery());

/**
 * The signed-in organisation's name: the tenant chosen at sign-in, read from the memberships
 * cache. Null while it loads, when it fails, or when no tenant is chosen (nothing is fetched).
 */
export const useActiveTenantName = (): string | null => {
  const tenantId = getActiveTenantId();
  const { data } = useQuery({
    ...tenantMembershipsQuery(),
    enabled: Boolean(tenantId),
    select: (memberships: MembershipRead[]) => activeTenantName(memberships, tenantId),
  });
  return data ?? null;
};

/**
 * Workspaces the caller explicitly belongs to inside `tenantId`. Shares one
 * cache entry (keyed by tenantId, `TENANT_CONTEXT_STALE_TIME` fresh) across
 * every consumer — the guard, the header dropdown, Home, the workspaces grid —
 * so only the first mount in that window hits the network; the rest read the
 * cache. A hard refresh clears the in-memory cache, so it refetches once.
 */
export const useTenantWorkspaces = (
  tenantId: string | null,
  enabled = true,
) =>
  useQuery({
    ...tenantWorkspacesQuery(tenantId ?? ""),
    enabled: enabled && Boolean(tenantId),
  });

/**
 * Imperative read for tenant selection, which must know the workspaces before
 * it navigates. Writes the same cache entry `useTenantWorkspaces` reads, so the
 * guard on the destination route resolves without a second request.
 */
export const useFetchTenantWorkspaces = () => {
  const queryClient = useQueryClient();
  return useCallback(
    (tenantId: string) =>
      queryClient.fetchQuery(tenantWorkspacesQuery(tenantId)),
    [queryClient],
  );
};

/** Select-tenant calls still running, by tenant: concurrent callers share one. */
const selectTenantInFlight = new Map<string, Promise<SelectTenantResponse>>();

/**
 * Imperative call for tenant selection: mints a fresh token scoped to the
 * chosen tenant. Not a query — this has a side effect (a new token) and must
 * run exactly once per "Continue" click, never cached or retried. Calls for
 * the same tenant while one is running share it (the login picker can mount
 * twice while it auto-continues); the next call after it settles mints anew.
 */
export const useSelectTenant = (): ((
  tenantId: string,
) => Promise<SelectTenantResponse>) =>
  useCallback((tenantId: string) => {
    const running = selectTenantInFlight.get(tenantId);
    if (running) return running;
    const call = selectTenant(tenantId).finally(() => {
      selectTenantInFlight.delete(tenantId);
    });
    selectTenantInFlight.set(tenantId, call);
    return call;
  }, []);
